import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
];

const PRIVATE = [["/founder", "Founder Office"], ["/investor-login", "Investor Portal"], ["/legal", "Legal Document Room"]];

const PORTFOLIO = [
  { to: "/portfolio", label: "Portfolio Overview", external: false },
  { to: "https://stewardandstone.jennaemichelle.com", label: "Steward & Stone", external: true },
  { to: "https://strategicassets.jennaemichelle.com", label: "Strategic Assets", external: true },
  { to: "https://nirvalife.com", label: "Nirva Life", external: true },
  { to: "https://foundation.jennaemichelle.com", label: "Jennae Michelle Foundation", external: true },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const ddRef = useRef(null);
  const accessRef = useRef(null);
  const toggleRef = useRef(null);
  const portfolioToggleRef = useRef(null);
  const headerRef = useRef(null);
  const closeMenus = () => { setOpen(false); setDdOpen(false); if (accessRef.current) accessRef.current.open = false; };

  // The shared header uses the approved dark Holdings surface on every route.
  const light = false;

  useEffect(() => {
    const header = headerRef.current;
    const measure = () => document.documentElement.style.setProperty("--jmh-header-height", `${header.getBoundingClientRect().height}px`);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(header); measure();
    return () => { observer?.disconnect(); document.documentElement.style.removeProperty("--jmh-header-height"); };
  }, []);

  useEffect(() => {
    setOpen(false);
    setDdOpen(false);
    if (accessRef.current) accessRef.current.open = false;
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false);
      if (accessRef.current && !accessRef.current.contains(e.target)) accessRef.current.open = false;
    };
    const onKey = (e) => {
      if (ddOpen && ddRef.current?.contains(document.activeElement) && ["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
        e.preventDefault();
        const links = [...ddRef.current.querySelectorAll('[role="menuitem"]')];
        const index = links.indexOf(document.activeElement);
        const next = e.key === "Home" ? 0 : e.key === "End" ? links.length - 1 : e.key === "ArrowDown" ? (index + 1) % links.length : (index - 1 + links.length) % links.length;
        links[next]?.focus();
      }
      if (e.key === "Escape") {
        if (accessRef.current?.open) { accessRef.current.open = false; accessRef.current.querySelector("summary")?.focus(); }
        if (ddRef.current?.contains(document.activeElement)) portfolioToggleRef.current?.focus();
        setDdOpen(false);
        setOpen((wasOpen) => {
          if (wasOpen) toggleRef.current?.focus();
          return false;
        });
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [ddOpen]);

  // The navigation is a non-modal disclosure. Keep document scrolling native;
  // restoring a captured body overflow value can strand another menu's lock.

  useEffect(() => {
    // Clear the mobile scroll lock when returning to the desktop header.
    const desktop = window.matchMedia("(min-width: 1100px)");
    const onChange = (event) => {
      if (event.matches) setOpen(false);
      else setDdOpen(false);
    };
    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, []);

  const inPortfolio =
    pathname.startsWith("/portfolio") ||
    pathname.startsWith("/steward-and-stone") ||
    pathname.startsWith("/strategic-assets") ||
    pathname.startsWith("/foundation");

  const linkCls = (active) => {
    if (light) {
      return `font-display uppercase tracking-wide2 text-[11px] link-underline transition-colors ${active ? "text-[var(--gold-deep)]" : "text-[var(--ink)] hover:text-[var(--gold-deep)]"}`;
    }
    return `font-display uppercase tracking-wide2 text-[11px] link-underline transition-colors ${active ? "text-[var(--gold-lite)]" : "text-[var(--bone)] hover:text-[var(--gold-lite)]"}`;
  };

  const headerScrolled = light
    ? "bg-[rgba(243,240,231,0.92)] backdrop-blur-md border-b border-[rgba(180,139,63,0.3)]"
    : "bg-[rgba(8,9,11,0.86)] backdrop-blur-md border-b border-[rgba(180,139,63,0.16)]";

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 transition-all duration-500 ${scrolled || open ? headerScrolled : "bg-transparent"}`}
      data-testid="site-nav"
      data-tone={light ? "light" : "dark"}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          onClick={closeMenus}
          className={`font-display uppercase tracking-wide2 text-sm ${light ? "text-[var(--ink)]" : "gold-text"}`}
          data-testid="nav-logo"
        >
          <img width={2172} height={724} decoding="async" src="/brand/holdings-wordmark-approved.webp" alt="Jennae Michelle Holdings" className="jmh-nav-wordmark" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} onClick={closeMenus} aria-current={pathname === l.to ? "page" : undefined} className={linkCls(pathname === l.to)} data-testid={`nav-link-${slug(l.label)}`}>
              {l.label}
            </Link>
          ))}

          <details className="relative" ref={accessRef} data-testid="nav-private-access">
            <summary className={`cursor-pointer ${linkCls(pathname.startsWith('/founder') || pathname === '/login' || pathname === '/legal' || pathname === '/investor-login')}`}>Private Access</summary>
            <div className="absolute right-0 mt-4 min-w-[240px] bg-[#08090b] border border-[rgba(180,139,63,0.22)] py-3 shadow-2xl">
              {PRIVATE.map(([to,label]) => <Link key={to} to={to} onClick={closeMenus} className="block px-5 py-3 font-display uppercase tracking-wide2 text-[10px] text-[var(--bone)] hover:text-[var(--gold-lite)]">{label}</Link>)}
            </div>
          </details>

          <div className="relative" ref={ddRef}>
            <button
              type="button"
              ref={portfolioToggleRef}
              aria-controls="nav-portfolio-menu"
              onClick={() => setDdOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={ddOpen}
              className={`flex items-center gap-1 ${linkCls(inPortfolio)}`}
              data-testid="nav-portfolio-trigger"
            >
              Portfolio
              <ChevronDown size={13} className={`transition-transform duration-300 ${ddOpen ? "rotate-180" : ""}`} />
            </button>
            {ddOpen && (
              <div
                className="absolute right-0 mt-4 min-w-[240px] bg-[rgba(8,9,11,0.98)] backdrop-blur-md border border-[rgba(180,139,63,0.22)] py-3 shadow-2xl"
                id="nav-portfolio-menu"
                role="menu"
                data-testid="nav-portfolio-menu"
              >
                {PORTFOLIO.map((p) =>
                  p.external ? (
                    <a
                      key={p.to}
                      href={p.to}
                      onClick={closeMenus}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className="block px-5 py-2.5 font-display uppercase tracking-wide2 text-[10px] text-[var(--bone)]/85 hover:text-[var(--gold-lite)] hover:bg-[rgba(180,139,63,0.06)] transition-colors"
                      data-testid={`nav-portfolio-${slug(p.label)}`}
                    >
                      {p.label}
                    </a>
                  ) : (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={closeMenus}
                      aria-current={pathname === p.to ? "page" : undefined}
                      role="menuitem"
                      className={`block px-5 py-2.5 font-display uppercase tracking-wide2 text-[10px] transition-colors hover:bg-[rgba(180,139,63,0.06)] ${pathname === p.to ? "text-[var(--gold-lite)]" : "text-[var(--bone)]/85 hover:text-[var(--gold-lite)]"}`}
                      data-testid={`nav-portfolio-${slug(p.label)}`}
                    >
                      {p.label}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>

          <Link to="/contact" onClick={closeMenus} aria-current={pathname === "/contact" ? "page" : undefined} className={linkCls(pathname === "/contact")} data-testid="nav-link-contact">
            Contact
          </Link>
        </nav>

        <button
          ref={toggleRef}
          className={`md:hidden ${light ? "text-[var(--ink)]" : "text-[var(--gold-lite)]"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="nav-mobile-menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav id="nav-mobile-menu" className="md:hidden bg-[#08090b] border-t border-[rgba(180,139,63,0.16)] px-6 py-6 flex flex-col gap-5 shadow-2xl" aria-label="Mobile" data-testid="nav-mobile-menu">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} onClick={closeMenus} aria-current={pathname === l.to ? "page" : undefined} className="font-display uppercase tracking-wide2 text-sm text-[var(--bone)]" data-testid={`nav-mobile-${slug(l.label)}`}>
              {l.label}
            </Link>
          ))}
          <div className="pt-1">
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80 mb-3">Private Access</p>
            <div className="flex flex-col gap-4 pl-3 border-l border-[rgba(180,139,63,0.22)]">
              {PRIVATE.map(([to,label]) => <Link key={to} to={to} onClick={closeMenus} className="font-display uppercase tracking-wide2 text-xs text-[var(--bone)]/85">{label}</Link>)}
            </div>
          </div>
          <div className="pt-1">
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80 mb-3">Portfolio</p>
            <div className="flex flex-col gap-4 pl-3 border-l border-[rgba(180,139,63,0.22)]">
              {PORTFOLIO.map((p) =>
                p.external ? (
                  <a key={p.to} href={p.to} onClick={closeMenus} target="_blank" rel="noopener noreferrer" className="font-display uppercase tracking-wide2 text-xs text-[var(--bone)]/85" data-testid={`nav-mobile-portfolio-${slug(p.label)}`}>
                    {p.label}
                  </a>
                ) : (
                  <Link key={p.to} to={p.to} onClick={closeMenus} aria-current={pathname === p.to ? "page" : undefined} className="font-display uppercase tracking-wide2 text-xs text-[var(--bone)]/85" data-testid={`nav-mobile-portfolio-${slug(p.label)}`}>
                    {p.label}
                  </Link>
                )
              )}
            </div>
          </div>
          <Link to="/contact" onClick={closeMenus} aria-current={pathname === "/contact" ? "page" : undefined} className="font-display uppercase tracking-wide2 text-sm text-[var(--bone)]" data-testid="nav-mobile-contact">
            Contact
          </Link>
        </nav>
      )}
    </header>
  );
};

